import React, { useEffect, useRef } from 'react';

// Define the props interface for the AppEmbed component
export interface AppEmbedProps {
  appId: string;
  apiKey: string;
  height?: string | number;
  width?: string | number;
  theme?: 'light' | 'dark' | string;
  onSubmit?: (data: any) => void;
  onValidate?: (data: any) => boolean;
  className?: string;
  style?: React.CSSProperties;
  baseUrl?: string;
  dataSources?: Record<string, any>;
}

// Declare the global DynamicFormsSDK interface
declare global {
  interface Window {
    DynamicFormsSDK: {
      init: (config: { baseUrl: string; apiKey: string }) => void;
      render: (
        containerId: string,
        options: {
          appId: string;
          schema?: any;
          height?: number | string;
          width?: number | string;
          theme?: string;
          dataSources?: Record<string, any>;
          onSubmit?: (data: any) => void;
          onValidate?: (data: any) => boolean;
        }
      ) => any;
    };
  }
}

export const AppEmbed: React.FC<AppEmbedProps> = ({
  appId,
  apiKey,
  height = '600px',
  width = '100%',
  theme,
  onSubmit,
  onValidate,
  className,
  style,
  baseUrl,
  dataSources,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const formInstanceRef = useRef<any>(null);
  const containerId = useRef(`app-embed-${Math.random().toString(36).substring(2, 11)}`);
  const effectiveBaseUrl = baseUrl || 'https://json-form-app.vercel.app';

  useEffect(() => {
    // Load the SDK script
    const script = document.createElement('script');
    // Use the baseUrl to construct the script URL
    script.src = `${effectiveBaseUrl}/embed.js`;
    script.async = true;
    
    // Function to initialize the form once the script is loaded
    const initializeForm = () => {
      if (!window.DynamicFormsSDK) {
        console.error('DynamicFormsSDK not loaded');
        return;
      }

      // Initialize the SDK with API key
      window.DynamicFormsSDK.init({
        baseUrl: effectiveBaseUrl,
        apiKey,
      });

      // Render the form
      formInstanceRef.current = window.DynamicFormsSDK.render(containerId.current, {
        appId,
        height,
        width,
        theme,
        dataSources,
        onSubmit,
        onValidate,
      });
    };

    // Set up script load handler
    script.onload = initializeForm;

    // Check if the SDK is already loaded
    if (window.DynamicFormsSDK) {
      initializeForm();
    } else {
      // Add the script to the document
      document.body.appendChild(script);
    }

    // Cleanup function
    return () => {
      if (formInstanceRef.current && formInstanceRef.current.destroy) {
        formInstanceRef.current.destroy();
      }
      // Remove the script if we added it
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [appId, apiKey, effectiveBaseUrl, dataSources]); // Re-initialize if these props change

  // Update form when other props change
  useEffect(() => {
    if (formInstanceRef.current && formInstanceRef.current.updateOptions) {
      formInstanceRef.current.updateOptions({
        height,
        width,
        theme,
        dataSources,
        onSubmit,
        onValidate,
      });
    }
  }, [height, width, theme, dataSources, onSubmit, onValidate]);

  // Apply height and width to container style
  const containerStyle: React.CSSProperties = {
    ...style,
    height: typeof height === 'number' ? `${height}px` : height,
    width: typeof width === 'number' ? `${width}px` : width,
  };

  return (
    <div
      id={containerId.current}
      ref={containerRef}
      className={className}
      style={containerStyle}
    />
  );
};

// Export other components or utilities if needed
export default AppEmbed;
